import { z } from "zod";

//
// Primitives
//

const PrimitiveValue = z.number().or(z.boolean()).or(z.string());
const ArrayValue = z.array(z.string());
const ObjectValue = z.record(z.string());

export const DTOExporterPropertyValue = PrimitiveValue.or(ArrayValue).or(ObjectValue);
export const DTOExporterPropertyType = z.enum(["Enum", "Boolean", "String", "Number", "Array", "Object", "Code"]);

export type DTOExporterPropertyDefinitionValue = z.infer<typeof DTOExporterPropertyValue>;
export type DTOExporterPropertyType = z.infer<typeof DTOExporterPropertyType>;

//
// Property
//

const PropertyDefinitionBase = z.object({
  key: z.string(),
  title: z.string(),
  description: z.string(),
  category: z.string().optional(),
  dependsOn: z.record(z.boolean()).optional(),
});

export type DTOPropertyDefinitionBase = z.infer<typeof PropertyDefinitionBase>;
export const DTOExporterPropertyDefinitionEnumOption = z.object({
  label: z.string(),
  description: z.string(),
});

export const DTOExporterPropertyDefinitionEnum = PropertyDefinitionBase.extend({
  type: z.literal(DTOExporterPropertyType.Enum.Enum),
  options: z.record(DTOExporterPropertyDefinitionEnumOption),
  default: z.string(),
});

export const DTOExporterPropertyDefinitionBoolean = PropertyDefinitionBase.extend({
  type: z.literal(DTOExporterPropertyType.Enum.Boolean),
  default: z.boolean(),
});

export const DTOExporterPropertyDefinitionString = PropertyDefinitionBase.extend({
  type: z.literal(DTOExporterPropertyType.Enum.String),
  default: z.string(),
  isMultiline: z.boolean().optional(),
});

export const DTOExporterPropertyDefinitionNumber = PropertyDefinitionBase.extend({
  type: z.literal(DTOExporterPropertyType.Enum.Number),
  default: z.number(),
});

export const DTOExporterPropertyDefinitionArray = PropertyDefinitionBase.extend({
  type: z.literal(DTOExporterPropertyType.Enum.Array),
  default: ArrayValue,
});

export const DTOExporterPropertyDefinitionObject = PropertyDefinitionBase.extend({
  type: z.literal(DTOExporterPropertyType.Enum.Object),
  default: ObjectValue,
  allowedKeys: z
    .object({
      options: z.string().array(),
      type: z.string(),
    })
    .optional(),
  allowedValues: z
    .object({
      type: z.string(),
    })
    .optional(),
});

export const DTOExporterPropertyDefinitionCode = PropertyDefinitionBase.extend({
  type: z.literal(DTOExporterPropertyType.Enum.Code),
  language: z.string(),
  default: z.string(),
});

export const DTOExporterPropertyDefinition = z.discriminatedUnion("type", [
  DTOExporterPropertyDefinitionEnum,
  DTOExporterPropertyDefinitionBoolean,
  DTOExporterPropertyDefinitionString,
  DTOExporterPropertyDefinitionNumber,
  DTOExporterPropertyDefinitionArray,
  DTOExporterPropertyDefinitionObject,
  DTOExporterPropertyDefinitionCode,
]);

export const DTOExporterPropertyDefinitionsResponse = z.object({
  properties: DTOExporterPropertyDefinition.array(),
});

export type DTOExporterPropertyDefinitionEnum = z.infer<typeof DTOExporterPropertyDefinitionEnum>;
export type DTOExporterPropertyDefinitionBoolean = z.infer<typeof DTOExporterPropertyDefinitionBoolean>;
export type DTOExporterPropertyDefinitionString = z.infer<typeof DTOExporterPropertyDefinitionString>;
export type DTOExporterPropertyDefinitionNumber = z.infer<typeof DTOExporterPropertyDefinitionNumber>;
export type DTOExporterPropertyDefinitionArray = z.infer<typeof DTOExporterPropertyDefinitionArray>;
export type DTOExporterPropertyDefinitionObject = z.infer<typeof DTOExporterPropertyDefinitionObject>;
export type DTOExporterPropertyDefinitionCode = z.infer<typeof DTOExporterPropertyDefinitionCode>;
export type DTOExporterPropertyDefinition = z.infer<typeof DTOExporterPropertyDefinition>;
export type DTOExporterPropertyDefinitionEnumOption = z.infer<typeof DTOExporterPropertyDefinitionEnumOption>;
export type DTOExporterPropertyDefinitionsResponse = z.infer<typeof DTOExporterPropertyDefinitionsResponse>;

//
// Value
//

export const DTOExporterPropertyValueMap = z.record(DTOExporterPropertyValue);
export type DTOExporterPropertyValueMap = z.infer<typeof DTOExporterPropertyValueMap>;
